import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { ChevronLeftIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { getTripDate } from '../../../api/trip-dates/get-trip-date'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import { DispatchRetainTables } from '../../../components/pages/trip-dates/dispatch-retain-tables'
import {
  LabeledItem,
  NumberOfPlacesBadge,
  TripDateDirectionalityBadge,
  TripDateEcartBadge
} from '../../../components/pages/trip-dates/trip-dates.ui'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import NotFoundPage from '../../common/not-found'

export function TripDateUpdatePage() {
  const params = useParams<{ id: string }>()
  const { back } = useNavigator()

  if (!params?.id || isNaN(+params?.id))
    return <ItemNotFound title="Identifiant de date de voyage invalide" />
  const id = Number(params.id)

  const {
    data: tripDate,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['trip-date', id],
    queryFn: () => getTripDate(id),
    staleTime: 0
  })

  const isTripDatePrimary = TripDateProperties.isPrimary(tripDate)
  const tripStepHHMM = TripDateProperties.getTripStepHHMM(tripDate)
  // const tripBookings = tripDate?.allocatedPlaces?.map((al) => al.tripBooking)

  if (isLoading) return <LoadingScreen />
  if (!tripDate) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={TripDateProperties.getTitle(tripDate)}
        icon={ChevronLeftIcon}
        onIconClick={() => back()}
      />
      <div className={'h-4'} />

      <Box className={'flex flex-col gap-4'}>
        {/* trip date details */}
        <InnerBox
          title={'Détails du voyage réservé'}
          className={'p-3 rounded-xl'}
          titleColor={tailwindColors.primary.DEFAULT}
        >
          <div className="bg-white-hover p-5 rounded-lg border border-grey-light flex flex-col gap-2">
            <div
              className={cn('gap-3', {
                'grid grid-cols-4': isTripDatePrimary,
                'flex flex-row justify-between': !isTripDatePrimary
              })}
            >
              <LabeledItem label={'Date du trajet'}>
                <Badge variant={'grey'} text={DateFormatter.DDMMYYYY(tripDate.date)} />
              </LabeledItem>

              <LabeledItem label={'Type de trajet'}>
                <TripDateDirectionalityBadge directionality={tripDate.directionality} />
              </LabeledItem>

              {isTripDatePrimary && (
                <LabeledItem
                  label={
                    tripDate.directionality === TripStepTypeEnum.INBOUND
                      ? 'Heure de départ'
                      : "Heure d'arrivée"
                  }
                >
                  {tripStepHHMM && <Badge variant={'grey'} text={tripStepHHMM} />}
                </LabeledItem>
              )}

              <LabeledItem label={'Voyages'}>
                <Typography variant={'secondary-body'}>
                  {TripProperties.getTripLabel(tripDate.trip)}
                </Typography>
              </LabeledItem>

              <LabeledItem label={'# Places réservées'}>
                <NumberOfPlacesBadge
                  studentsPlaces={tripDate.numberOfStudentsPlacesReserved}
                  staffPlaces={tripDate.numberOfStaffPlacesReserved}
                />
              </LabeledItem>

              {isTripDatePrimary && (
                <LabeledItem label={'# Places demandées'}>
                  <NumberOfPlacesBadge
                    studentsPlaces={tripDate.numberOfStudentsPlacesRequested}
                    staffPlaces={tripDate.numberOfStaffPlacesRequested}
                  />
                </LabeledItem>
              )}

              {!isTripDatePrimary && (
                <LabeledItem label={'# Places dispatchées'}>
                  <NumberOfPlacesBadge
                    studentsPlaces={tripDate.numberOfStudentsPlacesDispatched}
                    staffPlaces={tripDate.numberOfStaffPlacesDispatched}
                  />
                </LabeledItem>
              )}

              <LabeledItem label={'Écart'}>
                <TripDateEcartBadge tripDate={tripDate} />
              </LabeledItem>

              {isTripDatePrimary && (
                <LabeledItem label={'# Places dispatchées'}>
                  <NumberOfPlacesBadge
                    studentsPlaces={tripDate.numberOfStudentsPlacesDispatched}
                    staffPlaces={tripDate.numberOfStaffPlacesDispatched}
                  />
                </LabeledItem>
              )}
            </div>
          </div>
        </InnerBox>

        {/* dispatch retain tables */}
        <DispatchRetainTables
          tripDateId={tripDate.id}
          version={isTripDatePrimary ? 'PRIMARY' : 'SECONDARY'}
          onSuccess={() => refetch()}
          // hideDispatchTables={tripBookings && tripBookings.length <= 1} // only display the dispatch table when we have more than 1 trip bookings
        />
      </Box>
    </div>
  )
}
